import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export interface PanelProps {
  title?: string;
  /** Small grey line under the title (endpoint / provenance). */
  subtitle?: ReactNode;
  icon?: IconName;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** `flush` drops body padding (tables). */
  bodyClassName?: string;
  'aria-label'?: string;
}

/** Card/section shell used by every page so panels stay visually identical. */
export function Panel({
  title,
  subtitle,
  icon,
  actions,
  children,
  className = '',
  bodyClassName = 'p-panel',
  ...rest
}: PanelProps) {
  const heading = title ? (
    <div className="panel-head">
      <div className="flex items-center gap-2 min-w-0">
        {icon ? <Icon name={icon} className="text-ink-faint" /> : null}
        <div className="min-w-0">
          <h2 className="truncate text-ui font-semibold text-ink">{title}</h2>
          {subtitle ? (
            <p className="mt-0.5 truncate text-caption text-ink-faint">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  ) : null;

  return (
    <section className={`panel ${className}`} aria-label={rest['aria-label']}>
      {heading}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export interface KeyValueItem {
  label: string;
  value: ReactNode;
  /** Emphasise the value (metric) or dim it (unknown). */
  tone?: 'strong' | 'dim';
  /** `mono` marks the pair as an identifier row. */
  span?: 1 | 2;
}

/** Dense definition grid — identity/binding panels, activation steps. */
export function KeyValueGrid({ items }: { items: readonly KeyValueItem[] }) {
  return (
    <dl className="grid grid-cols-2 gap-x-block gap-y-2 m-0">
      {items.map((item) => (
        <div
          key={item.label}
          className={item.span === 2 ? 'col-span-2' : 'col-span-1'}
          data-testid="kv-row"
        >
          <dt className="text-label text-ink-faint">{item.label}</dt>
          <dd
            className={
              item.tone === 'strong'
                ? 'mt-0.5 text-ui font-semibold text-ink'
                : item.tone === 'dim'
                  ? 'mt-0.5 text-body text-ink-dim'
                  : 'mt-0.5 text-body text-ink'
            }
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function SectionTitle({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <h3 className="panel-title">{children}</h3>
      {hint ? <span className="text-caption text-ink-faint">{hint}</span> : null}
    </div>
  );
}
